import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Pencil, Users, Undo2, Archive } from 'lucide-react';

import useTrainingApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import Tooltip from '../../components/ui/Tooltip';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { unpublishTraining, archiveTraining } from '../../services/trainingService';

const Container = styled.div``;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  color: ${({ $color }) => $color || '#64748b'};
  transition: all 0.15s;
  &:hover { background: #F5F7FA; }
`;

export default function TrainingsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { canWrite } = useAuth();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [confirmUnpublish, setConfirmUnpublish] = useState(null); // holds {id, title}
  const [confirmArchive, setConfirmArchive] = useState(null);

  const { fetching, columns: baseColumns, data, pagination, refresh } = useTrainingApi(queryParams);

  const handleUnpublish = useCallback(async () => {
    if (!confirmUnpublish?.id) return;
    try {
      await unpublishTraining(confirmUnpublish.id);
      refresh?.();
    } finally {
      setConfirmUnpublish(null);
    }
  }, [confirmUnpublish, refresh]);

  const handleArchive = useCallback(async () => {
    if (!confirmArchive?.id) return;
    try {
      await archiveTraining(confirmArchive.id);
      refresh?.();
    } finally {
      setConfirmArchive(null);
    }
  }, [confirmArchive, refresh]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleRowClick = useCallback((row) => {
    if (!row?.id) return;
    if (row.status === 'draft') {
      history.push(`/trainings/${row.id}/edit`);
    } else if (row.status === 'published') {
      history.push(`/trainings/${row.id}/assignments`);
    }
  }, [history]);

  const columns = useMemo(() => {
    if (!canWrite('TR')) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            return (
              <ActionsCell>
                {row.status === 'draft' && (
                  <>
                    <Tooltip text={t('training.edit')}>
                      <IconBtn $color="#F59E0B" onClick={(e) => { e.stopPropagation(); history.push(`/trainings/${row.id}/edit`); }}>
                        <Pencil size={14} />
                      </IconBtn>
                    </Tooltip>
                    <Tooltip text={t('training.archive')}>
                      <IconBtn $color="#94A3B8" onClick={(e) => { e.stopPropagation(); setConfirmArchive({ id: row.id, title: row.title }); }}>
                        <Archive size={14} />
                      </IconBtn>
                    </Tooltip>
                  </>
                )}
                {row.status === 'published' && (
                  <>
                    <Tooltip text={t('training.assignments')}>
                      <IconBtn $color="#0291EF" onClick={(e) => { e.stopPropagation(); history.push(`/trainings/${row.id}/assignments`); }}>
                        <Users size={14} />
                      </IconBtn>
                    </Tooltip>
                    <Tooltip text={t('training.unpublish')}>
                      <IconBtn $color="#94A3B8" onClick={(e) => { e.stopPropagation(); setConfirmUnpublish({ id: row.id, title: row.title }); }}>
                        <Undo2 size={14} />
                      </IconBtn>
                    </Tooltip>
                    <Tooltip text={t('training.archive')}>
                      <IconBtn $color="#94A3B8" onClick={(e) => { e.stopPropagation(); setConfirmArchive({ id: row.id, title: row.title }); }}>
                        <Archive size={14} />
                      </IconBtn>
                    </Tooltip>
                  </>
                )}
              </ActionsCell>
            );
          },
        },
      },
    ];
  }, [baseColumns, canWrite, data, t, history]);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('training.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('TR')}
          newButtonLabel={t('training.new')}
          onNewButtonClick={() => history.push('/trainings/new')}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        filterList, handleFilterConfirm, handleDataStateChange,
        handleRowClick, history,
      ])}

      <ConfirmDialog
        isOpen={!!confirmUnpublish}
        title={t('training.unpublishConfirmTitle')}
        message={t('training.unpublishConfirmMessage', { title: confirmUnpublish?.title || '' })}
        confirmText={t('training.unpublish')}
        cancelText={t('common.cancel')}
        type="warning"
        onConfirm={handleUnpublish}
        onCancel={() => setConfirmUnpublish(null)}
      />

      <ConfirmDialog
        isOpen={!!confirmArchive}
        title={t('training.archiveConfirmTitle')}
        message={t('training.archiveConfirmMessage', { title: confirmArchive?.title || '' })}
        confirmText={t('training.archive')}
        cancelText={t('common.cancel')}
        type="warning"
        onConfirm={handleArchive}
        onCancel={() => setConfirmArchive(null)}
      />
    </Container>
  );
}
